import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Pagination,
  Button,
} from "@mui/material";
import { Warning, ArrowBack } from "@mui/icons-material";
import { PageHeader } from "../components/PageHeader";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { ClientSidePagination } from "../components/ClientSidePagination";
import { useErrorsPage } from "../hooks";
import { SYSTEM_ALLIANCE_ID } from "../store/slices/errorsSlice";
import { formatErrorMessageDisplay } from "../utils/errorMessage";

function errorCountChip(count: number) {
  if (count >= 50) {
    return (
      <Chip
        size="small"
        color="error"
        icon={<Warning fontSize="small" />}
        label={`${count}`}
      />
    );
  }
  if (count >= 10) {
    return <Chip size="small" color="warning" label={`${count}`} />;
  }
  if (count > 0) {
    return (
      <Chip
        size="small"
        color="primary"
        variant="outlined"
        label={`${count}`}
      />
    );
  }
  return <Chip size="small" variant="outlined" label="0" />;
}

function formatDate(value: string | null): string {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("hr-HR");
}

export function ErrorsPage() {
  const {
    summary,
    paginatedSummary,
    summaryPage,
    summaryPageSize,
    events,
    eventsTotal,
    eventsPage,
    eventsLimit,
    eventsTotalPages,
    selectedAllianceId,
    summaryLoading,
    eventsLoading,
    error,
    selectAlliance,
    changeEventsPage,
    changeSummaryPage,
  } = useErrorsPage();

  const selectedSummary = summary.find((row) =>
    row.allianceId == null
      ? selectedAllianceId === SYSTEM_ALLIANCE_ID
      : row.allianceId === selectedAllianceId,
  );

  return (
    <Box>
      <PageHeader
        title="Greške"
        subtitle="Pregled zabilježenih grešaka pri prikupljanju podataka po savezima. Odaberite entitet za detaljan popis događaja i dijagnostiku."
      />

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {selectedAllianceId && (
        <Button
          startIcon={<ArrowBack />}
          onClick={() => selectAlliance(null)}
          sx={{ mb: 2 }}
        >
          Natrag na sažetak
        </Button>
      )}

      {!selectedAllianceId ? (
        <Paper variant="outlined" sx={{ overflow: "hidden" }}>
          {summaryLoading ? (
            <LoadingSpinner py={6} />
          ) : summary.length === 0 ? (
            <Box sx={{ p: 4 }}>
              <Typography color="text.secondary">
                U zadnjih 14 dana nema zabilježenih grešaka.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Entitet</TableCell>
                    <TableCell>Sport</TableCell>
                    <TableCell>Broj grešaka</TableCell>
                    <TableCell>Zadnja greška</TableCell>
                    <TableCell align="right">Akcija</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedSummary.map((row) => (
                    <TableRow key={row.allianceId ?? "system"} hover>
                      <TableCell>{row.name ?? "Nepoznato"}</TableCell>
                      <TableCell>{row.sport ?? "-"}</TableCell>
                      <TableCell>{errorCountChip(row.errorCount)}</TableCell>
                      <TableCell>{formatDate(row.lastErrorAt)}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() =>
                            selectAlliance(row.allianceId ?? SYSTEM_ALLIANCE_ID)
                          }
                        >
                          Pregledaj
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <ClientSidePagination
            page={summaryPage}
            pageSize={summaryPageSize}
            total={summary.length}
            onPageChange={changeSummaryPage}
            aria-label="Stranice sažetka grešaka"
          />
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ overflow: "hidden" }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
            <Typography variant="h6">
              {selectedSummary?.name ?? "Odabrani savez"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedSummary?.sport ?? ""}
            </Typography>
          </Box>
          {eventsLoading ? (
            <LoadingSpinner py={6} />
          ) : events.length === 0 ? (
            <Box sx={{ p: 4 }}>
              <Typography color="text.secondary">
                Nema grešaka za odabrani savez.
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Vrijeme</TableCell>
                      <TableCell>Poruka</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {events.map((event) => (
                      <TableRow key={event._id} hover>
                        <TableCell>{formatDate(event.timestamp)}</TableCell>
                        <TableCell>
                          {formatErrorMessageDisplay(event.message)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {eventsTotalPages > 1 && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                    px: 2,
                    py: 2,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Prikazano {(eventsPage - 1) * eventsLimit + 1}-
                    {Math.min(eventsPage * eventsLimit, eventsTotal)} od{" "}
                    {eventsTotal}
                  </Typography>
                  <Pagination
                    count={eventsTotalPages}
                    page={eventsPage}
                    onChange={(_, page) => changeEventsPage(page)}
                    color="primary"
                    size="small"
                    siblingCount={0}
                    boundaryCount={1}
                    sx={{
                      "& .MuiPagination-ul": {
                        flexWrap: "wrap",
                        justifyContent: "center",
                      },
                    }}
                  />
                </Box>
              )}
              {eventsTotalPages <= 1 && eventsTotal > 0 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", textAlign: "center", pb: 2 }}
                >
                  Ukupno {eventsTotal} zapisa
                </Typography>
              )}
            </>
          )}
        </Paper>
      )}
    </Box>
  );
}
